import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL, resolveBackendPath, resolveUploadsPath, getLocalThumbnailUrl, getLocalImageUrl, getDriveThumbnailUrl, getDriveImageUrl } from '../config';

const statusCfg = {
  approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700' },
  pending:  { label: 'Pending',  cls: 'bg-amber-100 text-amber-700' },
  rejected: { label: 'Rejected', cls: 'bg-rose-100 text-rose-700' },
};

const Photos = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => { fetchPhotos(); }, []);
  useEffect(() => { setCurrentPage(1); }, [filter]);

  const fetchPhotos = async () => {
    try { const res = await axios.get(`${API_URL}/photos`); setPhotos(res.data); }
    catch (error) { console.error('Error fetching photos:', error); }
    finally { setLoading(false); }
  };

  const handleAction = async (photoId, action) => {
    if (!window.confirm(`${action === 'approve' ? 'Approve' : 'Reject'} this photo?`)) return;
    try { await axios.post(`${API_URL}/photos/${photoId}/${action}`); fetchPhotos(); }
    catch (error) { alert('Error: ' + (error.response?.data?.error || 'Unknown error')); }
  };

  const handleRetryEncoding = async (photoId) => {
    if (!window.confirm('Retry encoding for this photo?')) return;
    try { const res = await axios.post(`${API_URL}/photos/${photoId}/retry-encoding`); alert(res.data.message || 'Encoding successful!'); fetchPhotos(); }
    catch (error) { alert('Encoding failed: ' + (error.response?.data?.details || error.response?.data?.error || 'Unknown error')); }
  };

  const filteredPhotos = filter === 'all' ? photos : photos.filter(p => p.status === filter);
  const totalPages = Math.ceil(filteredPhotos.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentPhotos = filteredPhotos.slice(startIndex, startIndex + itemsPerPage);
  const goToPage = (page) => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const filterTabs = [
    { id: 'all', label: 'All', count: photos.length },
    { id: 'pending', label: 'Pending', count: photos.filter(p => p.status === 'pending').length },
    { id: 'approved', label: 'Approved', count: photos.filter(p => p.status === 'approved').length },
    { id: 'rejected', label: 'Rejected', count: photos.filter(p => p.status === 'rejected').length },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header + filter */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Photos</h1>
          <p className="text-sm text-slate-500 mt-0.5">{filteredPhotos.length} of {photos.length} photos</p>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
          {filterTabs.map((tab) => (
            <button key={tab.id} onClick={() => setFilter(tab.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${filter === tab.id ? 'bg-white shadow-sm text-violet-700' : 'text-slate-500 hover:text-slate-700'}`}>
              {tab.label} <span className={`ml-1 text-xs ${filter === tab.id ? 'text-violet-500' : 'text-slate-400'}`}>({tab.count})</span>
            </button>
          ))}
        </div>
      </div>

      {filteredPhotos.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center">
          <p className="text-slate-400 text-sm">No photos found</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
            {currentPhotos.map((photo) => {
              const cfg = statusCfg[photo.status] || statusCfg.pending;
              return (
                <div key={photo.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                  <div className="relative aspect-square">
                    <img
                      src={getLocalThumbnailUrl(photo) || (photo.drive_file_id ? getDriveThumbnailUrl(photo.drive_file_id) : resolveUploadsPath(photo.filepath))}
                      alt={photo.filename}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        if (photo.drive_file_id) { e.target.style.display = 'none'; }
                        else { const alt = resolveBackendPath(photo.filepath); if (alt && e.target.src !== alt) e.target.src = alt; else e.target.style.display = 'none'; }
                      }}
                    />
                    <button
                      onClick={() => window.open(getLocalImageUrl(photo) || getDriveImageUrl(photo.drive_file_id) || resolveUploadsPath(photo.filepath), '_blank')}
                      className="absolute top-1.5 right-1.5 p-1.5 rounded-lg bg-black/40 text-white hover:bg-black/60 transition-colors" title="View original">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    </button>
                  </div>
                  <div className="px-3 py-2.5 space-y-1.5">
                    <p className="text-xs font-semibold text-slate-800 truncate">{photo.filename}</p>
                    <p className="text-[10px] text-slate-400">{photo.user_name} · Batch #{photo.batch_number}</p>
                    <div className="flex flex-wrap gap-1">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cfg.cls}`}>{cfg.label}</span>
                      {photo.status === 'approved' && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${photo.is_encoded ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                          {photo.is_encoded ? 'Encoded' : 'Not Encoded'}
                        </span>
                      )}
                    </div>
                    {photo.status === 'pending' && (
                      <div className="flex gap-1">
                        <button onClick={() => handleAction(photo.id, 'approve')}
                          className="flex-1 py-1 rounded-lg text-[10px] font-bold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">Approve</button>
                        <button onClick={() => handleAction(photo.id, 'reject')}
                          className="flex-1 py-1 rounded-lg text-[10px] font-bold bg-rose-600 text-white hover:bg-rose-700 transition-colors">Reject</button>
                      </div>
                    )}
                    {photo.status === 'approved' && !photo.is_encoded && (
                      <button onClick={() => handleRetryEncoding(photo.id)}
                        className="w-full py-1 rounded-lg text-[10px] font-bold text-white transition-colors"
                        style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                        Retry Encoding
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1.5">
              <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}
                className="px-3 py-1.5 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">←</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => {
                if (pg === 1 || pg === totalPages || (pg >= currentPage - 1 && pg <= currentPage + 1)) {
                  return (
                    <button key={pg} onClick={() => goToPage(pg)}
                      className={`px-3 py-1.5 rounded-xl text-sm font-medium transition-all ${currentPage === pg ? 'text-white shadow-sm' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                      style={currentPage === pg ? { background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' } : {}}>
                      {pg}
                    </button>
                  );
                } else if (pg === currentPage - 2 || pg === currentPage + 2) {
                  return <span key={pg} className="text-slate-400 text-sm">…</span>;
                }
                return null;
              })}
              <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">→</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Photos;
